import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, ShieldCheck } from "lucide-react";

const PROVIDER_HELP = {
  v2: "reCAPTCHA v2 (\"I'm not a robot\" checkbox). Daftar di https://www.google.com/recaptcha/admin",
  v3: "reCAPTCHA v3 (skor otomatis 0–1, tanpa interaksi user). Isi Min Score (rekomendasi 0.5).",
  turnstile: "Cloudflare Turnstile (privacy-friendly, tanpa iklan). Daftar di https://dash.cloudflare.com/?to=/:account/turnstile",
};

export default function SuperAdminSecurity() {
  const [form, setForm] = useState({ enabled: false, provider: "v2", site_key: "", secret_key: "", min_score: 0.5 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { const r = await api.get("/security/captcha/admin"); setForm(prev => ({ ...prev, ...r.data })); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/security/captcha", {
        enabled: !!form.enabled, provider: form.provider,
        site_key: form.site_key || "", secret_key: form.secret_key || "",
        min_score: Number(form.min_score || 0.5),
      });
      toast.success("Konfigurasi captcha disimpan");
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  return (<>
    <PageHeader title="Keamanan — reCAPTCHA / Turnstile" subtitle="Konfigurasi captcha untuk halaman Login. Hanya Super Admin."/>
    <div className="p-4 md:p-8 max-w-3xl">
      <div className="card-flat p-5 space-y-5" data-testid="sec-captcha-form">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="brand-text"/>
          <h3 className="font-semibold text-slate-900">Captcha Provider</h3>
        </div>

        <div className="flex items-center justify-between border-b pb-3">
          <div>
            <div className="text-sm font-medium">Aktifkan Captcha</div>
            <div className="text-xs text-slate-500">Kalau aktif, semua login akan divalidasi.</div>
          </div>
          <Switch checked={!!form.enabled} onCheckedChange={(v)=>setForm({...form, enabled: v})} data-testid="sec-captcha-toggle"/>
        </div>

        <div className="space-y-1.5">
          <Label>Provider</Label>
          <Select value={form.provider} onValueChange={(v)=>setForm({...form, provider: v})}>
            <SelectTrigger data-testid="sec-captcha-provider"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="v2">Google reCAPTCHA v2 (checkbox)</SelectItem>
              <SelectItem value="v3">Google reCAPTCHA v3 (invisible, skor)</SelectItem>
              <SelectItem value="turnstile">Cloudflare Turnstile</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500">{PROVIDER_HELP[form.provider]}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Site Key (Client ID)</Label>
            <Input value={form.site_key || ""} onChange={upd("site_key")} placeholder="6Lc..." data-testid="sec-captcha-sitekey"/>
          </div>
          <div className="space-y-1.5">
            <Label>Secret Key</Label>
            <Input type="password" value={form.secret_key || ""} onChange={upd("secret_key")} placeholder="6Lc..." data-testid="sec-captcha-secret"/>
          </div>
        </div>

        {form.provider === "v3" && (
          <div className="space-y-1.5 max-w-xs">
            <Label>Min Score (0–1)</Label>
            <Input type="number" step="0.1" min="0" max="1" value={form.min_score} onChange={upd("min_score")} data-testid="sec-captcha-score"/>
            <p className="text-[11px] text-slate-500">Rekomendasi 0.5. Semakin tinggi = semakin ketat.</p>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={loading} className="brand-btn rounded-sm" data-testid="sec-captcha-save">
            <Save size={14} className="mr-1"/> {loading?"Menyimpan...":"Simpan"}
          </Button>
        </div>

        <div className="text-[11px] text-slate-500 bg-blue-50 border border-blue-200 rounded-sm p-3">
          <b>Fail-open behavior</b>: kalau captcha aktif tapi secret kosong / network error → login tetap jalan (untuk cegah lockout).
        </div>
      </div>
    </div>
  </>);
}
